import { useCallback, useEffect, useState } from 'react'

import { ProcurementTypeItemFragment } from 'generated/resources/graphql'

import { Form, useUpdateForm } from 'modules/forms'

interface UseFormStateResult {
  form: Form | undefined
  setForm: (form: Form) => void
  hasFormChanges: boolean
  isLastFormVersionPublished: boolean
  saveForm: () => Promise<{ success: boolean }>
  discardFormChanges: () => void
}

export function useFormState(
  requestType?: ProcurementTypeItemFragment,
  hookForm?: Form,
  recreateForm?: () => Form | undefined
): UseFormStateResult {
  // Local form state with dirty tracking
  const [localForm, setLocalForm] = useState<Form | undefined>(hookForm)
  const [hasFormChanges, setHasFormChanges] = useState(false)

  // Update form mutation
  const { updateForm } = useUpdateForm()

  // Update local form when hookForm changes (on load/refetch)
  useEffect(() => {
    if (hookForm) {
      setLocalForm(hookForm)
      setHasFormChanges(false) // Reset dirty state on fresh load
    }
  }, [hookForm])

  // Wrapped setter that marks form as dirty
  const handleSetForm = useCallback((newForm: Form) => {
    setLocalForm(newForm)
    setHasFormChanges(true) // Any setForm call = dirty
  }, [])

  // Save form and reset dirty flag on success
  const saveForm = useCallback(async () => {
    if (!localForm) {
      return { success: false }
    }

    const result = await updateForm(localForm)
    if (result.success) {
      setHasFormChanges(false)
    }
    return result
  }, [localForm, updateForm])

  // Discard local form changes by recreating a fresh Form from the immutable
  // GraphQL cache data. We can't reuse hookForm because the Form class is mutable
  // and the editor mutates it in place — hookForm is the same mutated object.
  const discardFormChanges = useCallback(() => {
    const freshForm = recreateForm?.()
    if (freshForm) {
      setLocalForm(freshForm)
      setHasFormChanges(false)
    }
  }, [recreateForm])

  // Get version metadata
  const sortList = requestType?.formsConnection?.nodes?.sort((a, b) => b.updatedAt - a.updatedAt)
  const lastRemoteFormRaw = sortList?.[0]

  return {
    form: localForm,
    setForm: handleSetForm,
    hasFormChanges,
    // TODO: Use form version instead of the active status
    isLastFormVersionPublished: lastRemoteFormRaw?.status === 'active',
    saveForm,
    discardFormChanges,
  }
}
